import React, { useState, useEffect, useCallback } from 'react';
import { Settings, Save, RotateCcw, Info } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const DEFAULTS = {
  message_identifier: 'KERALA BULLION',
  gold_22k_keyword: 'AKGSMA',
  gold_18k_keyword: '(18K/975)',
  silver_keyword: 'SILVER RATE',
  rate_pattern: '1gm',
};

export default function ParserConfigPanel() {
  const [config, setConfig] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await api.get('/kerala-bullion/parser-config');
      setConfig({
        message_identifier: res.data.message_identifier || DEFAULTS.message_identifier,
        gold_22k_keyword: res.data.gold_22k_keyword || DEFAULTS.gold_22k_keyword,
        gold_18k_keyword: res.data.gold_18k_keyword || DEFAULTS.gold_18k_keyword,
        silver_keyword: res.data.silver_keyword || DEFAULTS.silver_keyword,
        rate_pattern: res.data.rate_pattern || DEFAULTS.rate_pattern,
      });
    } catch (error) {
      // Use defaults
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/kerala-bullion/parser-config', config);
      toast.success('Parser config saved');
    } catch (error) {
      toast.error('Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setConfig(DEFAULTS);
    toast.info('Reset to defaults — click Save to apply');
  };

  if (loading) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="parser-config-panel">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
        data-testid="parser-config-toggle"
      >
        <div className="flex items-center gap-3">
          <Settings className="h-5 w-5 text-[#C9A967]" />
          <div className="text-left">
            <h3 className="font-semibold text-[#1a3a5c] text-sm">Message Parser Config</h3>
            <p className="text-xs text-gray-500">Configure keywords to match WhatsApp rate messages</p>
          </div>
        </div>
        <span className="text-gray-400 text-sm">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
            <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-800">
              These keywords tell the parser how to find rates in the WhatsApp message. 
              When the format changes, just update the keywords here — no code changes needed.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Message Identifier</label>
              <input
                type="text"
                value={config.message_identifier}
                onChange={(e) => setConfig({ ...config, message_identifier: e.target.value })}
                placeholder="e.g. KERALA BULLION"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#C9A967] focus:border-transparent"
                data-testid="config-message-identifier"
              />
              <p className="text-[10px] text-gray-400 mt-1">How to identify this is a rate message</p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Rate Pattern</label>
              <input
                type="text"
                value={config.rate_pattern}
                onChange={(e) => setConfig({ ...config, rate_pattern: e.target.value })}
                placeholder="e.g. 1gm"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#C9A967] focus:border-transparent"
                data-testid="config-rate-pattern"
              />
              <p className="text-[10px] text-gray-400 mt-1">Text before the rate number (e.g. "1gm:13285")</p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">22K Gold Keyword</label>
              <input
                type="text"
                value={config.gold_22k_keyword}
                onChange={(e) => setConfig({ ...config, gold_22k_keyword: e.target.value })}
                placeholder="e.g. AKGSMA"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#C9A967] focus:border-transparent"
                data-testid="config-gold-22k"
              />
              <p className="text-[10px] text-gray-400 mt-1">Keyword before the 22K gold rate</p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">18K Gold Keyword</label>
              <input
                type="text"
                value={config.gold_18k_keyword}
                onChange={(e) => setConfig({ ...config, gold_18k_keyword: e.target.value })}
                placeholder="e.g. (18K/975)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#C9A967] focus:border-transparent"
                data-testid="config-gold-18k"
              />
              <p className="text-[10px] text-gray-400 mt-1">Keyword before the 18K gold rate</p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Silver Keyword</label>
              <input
                type="text"
                value={config.silver_keyword}
                onChange={(e) => setConfig({ ...config, silver_keyword: e.target.value })}
                placeholder="e.g. SILVER RATE"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#C9A967] focus:border-transparent"
                data-testid="config-silver"
              />
              <p className="text-[10px] text-gray-400 mt-1">Keyword before the silver rate</p>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-[#1a3a5c] text-white rounded-lg text-sm hover:bg-[#1f4268] disabled:opacity-50 transition-colors"
              data-testid="save-parser-config"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save Config'}
            </button>
            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 transition-colors"
              data-testid="reset-parser-config"
            >
              <RotateCcw className="h-4 w-4" />
              Reset Defaults
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
